import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getToken, setToken, removeToken, parseJwtPayload, useSessionStore } from '@omniflow/browser-bus'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(getToken())

  const isAuthenticated = computed(() => token.value !== null)

  const claims = computed(() => {
    if (!token.value) return null
    return parseJwtPayload(token.value)
  })

  const tenantId = computed(() =>
    (claims.value?.tenantId as string) ?? null,
  )

  const username = computed(() =>
    (claims.value?.username as string) ?? (claims.value?.sub as string) ?? null,
  )

  /** UUID пользователя (JWT `sub`) — им же сервер штампует identity актора (DUOACC). */
  const userId = computed(() => (claims.value?.sub as string) ?? null)

  const roles = computed(() =>
    (claims.value?.roles as string[]) ?? [],
  )

  /** Имя текущего тенанта (для покраски хедера). В JWT с момента логина. */
  const tenantName = computed(() =>
    (claims.value?.tenantName as string) ?? null,
  )

  /** Явный цвет хедера тенанта (#RRGGBB) или null = авто-цвет из имени. */
  const tenantColor = computed(() =>
    (claims.value?.tenantColor as string | null) ?? null,
  )

  /**
   * Новый токен от сервера (логин, перелогин): в стор и в хранилище вместе с порогом
   * продления (SESSRENEW R2). Без `renewAfterSec` keeper берёт фолбэк 1 ч.
   */
  const saveToken = (jwt: string, renewAfterSec?: number) => {
    token.value = jwt
    setToken(jwt, renewAfterSec === undefined ? undefined : { renewAfterSec })
    // Перелогин в модалке: значок не должен до тика keeper'а (30 с) держать «Сессия истекла».
    useSessionStore().sync()
  }

  /**
   * Принять токен, который УЖЕ лежит в хранилище (продлила эта или другая вкладка,
   * перелогин того же пользователя в соседней) — только ref, без записи: повторная
   * запись сдвинула бы отметку получения и расписание продления.
   */
  const adoptToken = (jwt: string) => {
    token.value = jwt
    useSessionStore().sync()
  }

  const logout = () => {
    token.value = null
    removeToken()
  }

  return {
    token, isAuthenticated, claims,
    tenantId, username, userId, roles,
    tenantName, tenantColor,
    saveToken, adoptToken, logout,
  }
})
